import React, { useRef, useEffect, useState } from 'react';
import { motion, useAnimation } from 'framer-motion';
import { twMerge } from 'tailwind-merge';

export interface HorizontalSliderProps {
  children: React.ReactNode[];
  className?: string;
  onSlideChange?: (index: number) => void;
  initialSlide?: number;
}

const HorizontalSlider: React.FC<HorizontalSliderProps> = ({
  children,
  className,
  onSlideChange,
  initialSlide = 0,
}) => {
  const slidesCount = React.Children.count(children);
  const containerRef = useRef<HTMLDivElement>(null);
  const slideContainerRef = useRef<HTMLDivElement>(null);
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isPerfectlyCentered, setIsPerfectlyCentered] = useState(false);
  const [hasReachedStart, setHasReachedStart] = useState(false);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [scrollMode, setScrollMode] = useState<'vertical' | 'horizontal' | 'locked'>('vertical');
  const [previousScroll, setPreviousScroll] = useState(0);
  const [hasBeenCentered, setHasBeenCentered] = useState(false);
  const [hasCapturedScroll, setHasCapturedScroll] = useState(false);
  const controls = useAnimation();
  const lastScrollTime = useRef<number>(0);
  const wheelEvents = useRef<WheelEvent[]>([]);
  const scrollThreshold = 30;
  const transitionDuration = 0.8;
  const perfectCenterObserver = useRef<IntersectionObserver | null>(null);
  const earlyDetectionObserver = useRef<IntersectionObserver | null>(null);
  const activationTimeout = useRef<NodeJS.Timeout | null>(null);
  const centeringTimeout = useRef<NodeJS.Timeout | null>(null);
  const scrollPosition = useRef<number>(0);
  const scrollTimer = useRef<NodeJS.Timeout | null>(null);
  const originalOverflowY = useRef<string>('');

  // Store original scroll position when component mounts
  useEffect(() => {
    originalOverflowY.current = document.body.style.overflowY;
    return () => {
      // Restore original overflow when component unmounts
      document.body.style.overflowY = originalOverflowY.current;
    };
  }, []);

  // Set initial slide on first render
  useEffect(() => {
    if (initialSlide > 0 && initialSlide < slidesCount) {
      controls.start({ x: `${-initialSlide * 100}%` });
    }
  }, [initialSlide, slidesCount, controls]);

  // Early detection when slider is approaching
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '100px 0px',
      threshold: 0.1, // Detect when slider is getting close
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasCapturedScroll) {
          setIsActive(true);
          
          // Capture the current scroll position
          scrollPosition.current = window.scrollY;
        }
      });
    }, options);

    if (containerRef.current) {
      observer.observe(containerRef.current);
      earlyDetectionObserver.current = observer;
    }

    return () => {
      if (containerRef.current && observer) {
        observer.unobserve(containerRef.current);
      }
    };
  }, [hasCapturedScroll]);

  // Force-center the slider when it becomes visible
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5, // Slider is about halfway in view
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && !hasBeenCentered && !isTransitioning) {
          // Force center the slider
          if (containerRef.current) {
            setIsTransitioning(true);
            setScrollMode('locked');
            
            // Temporarily disable scrolling
            document.body.style.overflowY = 'hidden';
            
            // Check if the slider is already well-centered (page was loaded with slider in view)
            const rect = containerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const elementCenter = rect.top + rect.height / 2;
            const viewportCenter = windowHeight / 2;
            const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
            
            // If already close to center (within 5% of viewport height), skip the animation
            if (distanceFromCenter < windowHeight * 0.05) {
              // Skip scroll animation and just update state
              if (centeringTimeout.current) {
                clearTimeout(centeringTimeout.current);
              }
              
              setIsPerfectlyCentered(true);
              setHasBeenCentered(true);
              setScrollMode('horizontal');
              setIsTransitioning(false);
              setHasCapturedScroll(true);
              setHasReachedStart(currentSlide === 0);
              
              // Update the scroll position reference
              scrollPosition.current = window.scrollY;
              
              // Re-enable scrolling after a short delay
              setTimeout(() => {
                document.body.style.overflowY = originalOverflowY.current;
              }, 100);
            } else {
              // Calculate the exact scroll position to center the slider
              const scrollTarget = window.scrollY + rect.top - (windowHeight - rect.height) / 2;
              
              // Smooth scroll to center
              window.scrollTo({
                top: scrollTarget,
                behavior: 'smooth'
              });
              
              // After scroll completes, set centered state
              if (centeringTimeout.current) {
                clearTimeout(centeringTimeout.current);
              }
              
              centeringTimeout.current = setTimeout(() => {
                setIsPerfectlyCentered(true);
                setHasBeenCentered(true);
                setScrollMode('horizontal');
                setIsTransitioning(false);
                setHasCapturedScroll(true);
                setHasReachedStart(currentSlide === 0);
                
                // Update the scroll position reference
                scrollPosition.current = window.scrollY;
              }, 600);
            }
          }
        }
      });
    }, options);

    if (containerRef.current && !hasBeenCentered) {
      observer.observe(containerRef.current);
      perfectCenterObserver.current = observer;
    }

    return () => {
      if (containerRef.current && observer) {
        observer.unobserve(containerRef.current);
      }
      if (centeringTimeout.current) {
        clearTimeout(centeringTimeout.current);
      }
    };
  }, [hasBeenCentered, isTransitioning, currentSlide]);

  // Prevent scroll-jacking by listening to scroll events
  useEffect(() => {
    // Add a safety timeout to ensure scroll is unlocked even if other logic fails
    const safetyTimeout = setTimeout(() => {
      if (document.body.style.overflowY === 'hidden') {
        document.body.style.overflowY = originalOverflowY.current;
      }
    }, 2000);

    const handleScroll = () => {
      if (!hasBeenCentered && isActive) {
        // If the slider is active but not yet centered, force center it
        if (scrollTimer.current) {
          clearTimeout(scrollTimer.current);
        }
        
        scrollTimer.current = setTimeout(() => {
          if (containerRef.current && !isTransitioning) {
            setIsTransitioning(true);
            setScrollMode('locked');
            
            // Temporarily disable scrolling
            document.body.style.overflowY = 'hidden';
            
            // Check if the slider is already well-centered
            const rect = containerRef.current.getBoundingClientRect();
            const windowHeight = window.innerHeight;
            const elementCenter = rect.top + rect.height / 2;
            const viewportCenter = windowHeight / 2;
            const distanceFromCenter = Math.abs(elementCenter - viewportCenter);
            
            // If already close to center, skip the animation
            if (distanceFromCenter < windowHeight * 0.05) {
              setIsPerfectlyCentered(true);
              setHasBeenCentered(true);
              setScrollMode('horizontal');
              setIsTransitioning(false);
              setHasCapturedScroll(true);
              
              // Update the scroll position reference
              scrollPosition.current = window.scrollY;
              
              // Re-enable scrolling after a short delay
              setTimeout(() => {
                document.body.style.overflowY = originalOverflowY.current;
              }, 100);
            } else {
              // Calculate the exact scroll position to center the slider
              const scrollTarget = window.scrollY + rect.top - (windowHeight - rect.height) / 2;
              
              // Smooth scroll to center
              window.scrollTo({
                top: scrollTarget,
                behavior: 'smooth'
              });
              
              // After scroll completes, set centered state
              if (centeringTimeout.current) {
                clearTimeout(centeringTimeout.current);
              }
              
              centeringTimeout.current = setTimeout(() => {
                setIsPerfectlyCentered(true);
                setHasBeenCentered(true);
                setScrollMode('horizontal');
                setIsTransitioning(false);
                setHasCapturedScroll(true);
                
                // Update the scroll position reference
                scrollPosition.current = window.scrollY;
              }, 600);
            }
          }
        }, 50);
      } else if (isPerfectlyCentered && scrollMode === 'horizontal' && currentSlide !== slidesCount - 1) {
        // If we're in horizontal mode and not at the last slide, prevent vertical scrolling
        window.scrollTo(0, scrollPosition.current);
      }
    };
    
    window.addEventListener('scroll', handleScroll, { passive: false });
    
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (scrollTimer.current) {
        clearTimeout(scrollTimer.current);
      }
      clearTimeout(safetyTimeout);
    };
  }, [isActive, hasBeenCentered, isPerfectlyCentered, scrollMode, isTransitioning, currentSlide, slidesCount]);

  // Handle wheel events - control horizontal scrolling when centered
  useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      // If the slider is active and centered, and we're in horizontal mode
      if (isActive && isPerfectlyCentered && scrollMode === 'horizontal') {
        // If at first slide and scrolling up, stay in horizontal mode
        if (currentSlide === 0 && e.deltaY < 0) {
          e.preventDefault();
          return;
        }

        // If at last slide and scrolling down, exit horizontal mode
        if (currentSlide === slidesCount - 1 && e.deltaY > 0) {
          setScrollMode('vertical');
          setIsPerfectlyCentered(false);
          // Re-enable vertical scrolling
          document.body.style.overflowY = originalOverflowY.current;
          return;
        }

        // Prevent default scrolling in horizontal mode
        e.preventDefault();
        
        if (isTransitioning) return;
        
        const now = Date.now();
        const timeSinceLastScroll = now - lastScrollTime.current;
        
        // Clear old events (older than 300ms)
        if (timeSinceLastScroll > 300) {
          wheelEvents.current = [];
        }
        
        // Add current event to the queue
        wheelEvents.current.push(e);
        lastScrollTime.current = now;
        
        // Calculate accumulated delta
        const accumulatedDeltaY = wheelEvents.current.reduce((acc, event) => acc + event.deltaY, 0);
        
        // Check if we've accumulated enough movement to change slides
        if (Math.abs(accumulatedDeltaY) >= scrollThreshold) {
          const direction = accumulatedDeltaY > 0 ? 1 : -1;
          
          // Clear events queue
          wheelEvents.current = [];
          
          // Only proceed if we're not at boundaries
          if (
            (direction > 0 && currentSlide < slidesCount - 1) ||
            (direction < 0 && currentSlide > 0)
          ) {
            const nextSlide = currentSlide + direction;
            
            // Start transition
            setIsTransitioning(true);
            
            // Animate to next slide
            controls.start({ 
              x: `${-nextSlide * 100}%` 
            }, { 
              duration: transitionDuration,
              ease: [0.32, 0.72, 0.29, 0.97], // Custom ease with slight bounce
            }).then(() => {
              setCurrentSlide(nextSlide);
              setIsTransitioning(false);
              
              // Reset boundary flags
              setHasReachedStart(nextSlide === 0);
              setHasReachedEnd(nextSlide === slidesCount - 1);
              
              if (onSlideChange) {
                onSlideChange(nextSlide);
              }
            });
          }
        }
      } else if (isActive && !hasBeenCentered) {
        // If the slider is active but not yet centered, we should prevent scrolling past it
        e.preventDefault();
        
        // Force center the slider
        if (containerRef.current && !isTransitioning) {
          setIsTransitioning(true);
          setScrollMode('locked');
          
          // Calculate the exact scroll position to center the slider
          const rect = containerRef.current.getBoundingClientRect();
          const windowHeight = window.innerHeight;
          const scrollTarget = window.scrollY + rect.top - (windowHeight - rect.height) / 2;
          
          // Smooth scroll to center
          window.scrollTo({
            top: scrollTarget,
            behavior: 'smooth'
          });
          
          // After scroll completes, set centered state
          setTimeout(() => {
            setIsPerfectlyCentered(true);
            setHasBeenCentered(true);
            setScrollMode('horizontal');
            setIsTransitioning(false);
            
            // Update the scroll position reference
            scrollPosition.current = window.scrollY;
          }, 600);
        }
      }
    };
    
    window.addEventListener('wheel', handleWheel, { passive: false });
    
    return () => {
      window.removeEventListener('wheel', handleWheel);
    };
  }, [currentSlide, isTransitioning, slidesCount, controls, onSlideChange, isActive, isPerfectlyCentered, scrollMode, hasBeenCentered]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!isActive || !isPerfectlyCentered || scrollMode !== 'horizontal' || isTransitioning) return;
      
      if ((e.key === 'ArrowDown' || e.key === 'ArrowRight') && currentSlide < slidesCount - 1) {
        e.preventDefault();
        const nextSlide = currentSlide + 1;
        
        setIsTransitioning(true);
        controls.start({ 
          x: `${-nextSlide * 100}%` 
        }, { 
          duration: transitionDuration,
          ease: [0.32, 0.72, 0.29, 0.97]
        }).then(() => {
          setCurrentSlide(nextSlide);
          setIsTransitioning(false);
          setHasReachedEnd(nextSlide === slidesCount - 1);
          
          if (onSlideChange) {
            onSlideChange(nextSlide);
          }
          
          // If we've reached the last slide, prepare to exit horizontal mode
          if (nextSlide === slidesCount - 1) {
            document.body.style.overflowY = originalOverflowY.current;
          }
        });
      } else if ((e.key === 'ArrowUp' || e.key === 'ArrowLeft') && currentSlide > 0) {
        e.preventDefault();
        const nextSlide = currentSlide - 1;
        
        setIsTransitioning(true);
        controls.start({ 
          x: `${-nextSlide * 100}%` 
        }, { 
          duration: transitionDuration,
          ease: [0.32, 0.72, 0.29, 0.97]
        }).then(() => {
          setCurrentSlide(nextSlide);
          setIsTransitioning(false);
          setHasReachedStart(nextSlide === 0);
          
          if (onSlideChange) {
            onSlideChange(nextSlide);
          }
        });
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [currentSlide, isTransitioning, slidesCount, controls, onSlideChange, isActive, isPerfectlyCentered, scrollMode]);

  // Handle touch events
  useEffect(() => {
    let touchStartX = 0;
    let touchStartY = 0;
    
    const handleTouchStart = (e: TouchEvent) => {
      if (!isActive || !isPerfectlyCentered || scrollMode !== 'horizontal') return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
    };
    
    const handleTouchMove = (e: TouchEvent) => {
      if (isActive && !hasBeenCentered) {
        // If slider is active but not centered, prevent scrolling
        e.preventDefault();
        return;
      }
      
      if (!isActive || !isPerfectlyCentered || scrollMode !== 'horizontal') return;

      // If at last slide and trying to go forward, exit horizontal mode
      if (currentSlide === slidesCount - 1) {
        const touchY = e.touches[0].clientY;
        if (touchY < touchStartY - 30) { // Swiping up/forward significantly
          setScrollMode('vertical');
          setIsPerfectlyCentered(false);
          document.body.style.overflowY = originalOverflowY.current;
          return;
        }
      }

      if (isTransitioning) {
        e.preventDefault();
        return;
      }
      
      const touchX = e.touches[0].clientX;
      const touchY = e.touches[0].clientY;
      const diffX = touchStartX - touchX;
      const diffY = touchStartY - touchY;
      
      // Check if the swipe is more vertical than horizontal
      if (Math.abs(diffX) < Math.abs(diffY)) {
        // Vertical swipe
        if (Math.abs(diffY) > 70) { // Threshold to consider it a swipe
          if (diffY > 0 && currentSlide < slidesCount - 1) {
            // Swipe up - go to next slide
            e.preventDefault();
            const nextSlide = currentSlide + 1;
            
            setIsTransitioning(true);
            controls.start({ 
              x: `${-nextSlide * 100}%` 
            }, { 
              duration: transitionDuration,
              ease: [0.32, 0.72, 0.29, 0.97]
            }).then(() => {
              setCurrentSlide(nextSlide);
              setIsTransitioning(false);
              setHasReachedEnd(nextSlide === slidesCount - 1);
              
              if (onSlideChange) {
                onSlideChange(nextSlide);
              }
              
              // If we've reached the last slide, prepare to exit horizontal mode
              if (nextSlide === slidesCount - 1) {
                document.body.style.overflowY = originalOverflowY.current;
              }
            });
            
            touchStartY = touchY; // Reset touch start
          } else if (diffY < 0 && currentSlide > 0) {
            // Swipe down - go to previous slide
            e.preventDefault();
            const nextSlide = currentSlide - 1;
            
            setIsTransitioning(true);
            controls.start({ 
              x: `${-nextSlide * 100}%` 
            }, { 
              duration: transitionDuration,
              ease: [0.32, 0.72, 0.29, 0.97]
            }).then(() => {
              setCurrentSlide(nextSlide);
              setIsTransitioning(false);
              setHasReachedStart(nextSlide === 0);
              
              if (onSlideChange) {
                onSlideChange(nextSlide);
              }
            });
            
            touchStartY = touchY; // Reset touch start
          }
        }
      }
    };
    
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    
    return () => {
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
    };
  }, [currentSlide, isTransitioning, slidesCount, controls, onSlideChange, isActive, isPerfectlyCentered, scrollMode, hasBeenCentered]);

  // Function to handle dot navigation
  const goToSlide = (index: number) => {
    if (isTransitioning || index === currentSlide || !isPerfectlyCentered) return;
    
    setIsTransitioning(true);
    controls.start({ 
      x: `${-index * 100}%` 
    }, { 
      duration: transitionDuration,
      ease: [0.32, 0.72, 0.29, 0.97]
    }).then(() => {
      setCurrentSlide(index);
      setIsTransitioning(false);
      setHasReachedStart(index === 0);
      setHasReachedEnd(index === slidesCount - 1);
      
      if (onSlideChange) {
        onSlideChange(index);
      }
      
      // If we've reached the last slide, prepare to exit horizontal mode
      if (index === slidesCount - 1) {
        document.body.style.overflowY = originalOverflowY.current;
      }
    });
  };

  return (
    <div
      ref={containerRef}
      className={twMerge("tw-relative tw-h-screen tw-overflow-hidden", className)}
      id="horizontal-slider"
      data-active={isActive ? "true" : "false"}
      data-centered={isPerfectlyCentered ? "true" : "false"}
      data-scroll-mode={scrollMode}
    >
      <motion.div
        ref={slideContainerRef}
        className="tw-flex tw-h-full tw-w-full"
        animate={controls}
        initial={{ x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        {React.Children.map(children, (child, index) => (
          <div 
            key={index} 
            className="tw-w-screen tw-h-screen tw-flex-shrink-0"
            data-slide-index={index}
          >
            {child}
          </div>
        ))}
      </motion.div>
      
      {/* Status indicator for slide position */}
      {isActive && (
        <div className="tw-absolute tw-top-4 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-full tw-px-3 tw-py-1 tw-text-white tw-text-sm tw-font-medium tw-flex tw-items-center tw-space-x-2">
          {!isPerfectlyCentered && !isTransitioning && (
            <span className="tw-flex tw-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-mr-1 tw-animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Chargement...
            </span>
          )}
          {isTransitioning && (
            <span className="tw-flex tw-items-center">
              <svg className="tw-animate-spin tw-h-4 tw-w-4 tw-mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="tw-opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="tw-opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Centrage automatique...
            </span>
          )}
          {isPerfectlyCentered && currentSlide === 0 && !isTransitioning && (
            <span>Défilement horizontal actif</span>
          )}
          {isPerfectlyCentered && currentSlide > 0 && currentSlide < slidesCount - 1 && !isTransitioning && (
            <span>Défilement horizontal actif</span>
          )}
          {isPerfectlyCentered && currentSlide === slidesCount - 1 && !isTransitioning && (
            <span className="tw-flex tw-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
              </svg>
              Défiler vers le bas pour continuer
            </span>
          )}
        </div>
      )}
      
      {/* Navigation indicators */}
      <div className={`tw-absolute tw-bottom-8 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-flex tw-space-x-3 tw-z-10 ${isPerfectlyCentered ? 'tw-opacity-100' : 'tw-opacity-50'} tw-transition-opacity tw-duration-300`}>
        {Array.from({ length: slidesCount }).map((_, index) => (
          <button
            key={index}
            className={`tw-w-3 tw-h-3 tw-rounded-full tw-transition-colors tw-duration-300 ${
              index === currentSlide ? 'tw-bg-white' : 'tw-bg-white/40'
            }`}
            onClick={() => goToSlide(index)}
            disabled={!isPerfectlyCentered || isTransitioning}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>
      
      {/* Left/Right navigation arrows - only show when perfectly centered */}
      {isActive && isPerfectlyCentered && currentSlide > 0 && !isTransitioning && (
        <button 
          className="tw-absolute tw-left-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-white/10 tw-text-white tw-p-3 tw-rounded-full tw-z-10 hover:tw-bg-white/20 tw-transition-colors"
          onClick={() => goToSlide(currentSlide - 1)}
          disabled={isTransitioning}
          aria-label="Previous slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      )}
      
      {isActive && isPerfectlyCentered && currentSlide < slidesCount - 1 && !isTransitioning && (
        <button 
          className="tw-absolute tw-right-4 tw-top-1/2 tw-transform tw--translate-y-1/2 tw-bg-white/10 tw-text-white tw-p-3 tw-rounded-full tw-z-10 hover:tw-bg-white/20 tw-transition-colors"
          onClick={() => goToSlide(currentSlide + 1)}
          disabled={isTransitioning}
          aria-label="Next slide"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-6 tw-w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      )}
      
      {/* Progress indicator */}
      <div className={`tw-absolute tw-top-4 tw-right-4 tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-full tw-px-3 tw-py-1 tw-text-white tw-text-sm tw-font-medium tw-transition-opacity tw-duration-300 ${isPerfectlyCentered ? 'tw-opacity-100' : 'tw-opacity-50'}`}>
        {currentSlide + 1} / {slidesCount}
      </div>
      
      {/* Scroll instruction when centered */}
      {isPerfectlyCentered && !isTransitioning && (
        <div className="tw-absolute tw-bottom-20 tw-left-1/2 tw-transform tw--translate-x-1/2 tw-bg-white/10 tw-backdrop-blur-sm tw-rounded-full tw-px-4 tw-py-2 tw-text-white tw-text-sm tw-font-medium tw-animate-pulse">
          {currentSlide === 0 && (
            <span className="tw-flex tw-items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="tw-h-4 tw-w-4 tw-mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
              Défiler vers le bas pour voir la suite
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default HorizontalSlider;
